import React from "react";
import { useNavigate } from "react-router-dom";

interface DelegationBasicInfoProps {
  formData: {
    country: string;
    organization: string;
    fromDate: string;
    toDate: string;
    quarter: string;
    delegationName: string;
    budget: string;
    budgetSource: string;
  };
  handleInputChange: (field: string, value: string) => void;
}

const DelegationBasicInfo: React.FC<DelegationBasicInfoProps> = ({
  formData,
  handleInputChange,
}) => {
  const navigate = useNavigate();
  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      {/* First Row - Five Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
        {/* Chọn Quốc gia */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Chọn Quốc gia <span className="text-red-500">(*)</span>
          </label>
          <select
            value={formData.country}
            onChange={(e) => handleInputChange("country", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Quốc gia -</option>
            <option value="vietnam">Việt Nam</option>
            <option value="usa">Hoa Kỳ</option>
            <option value="japan">Nhật Bản</option>
            <option value="south-korea">Hàn Quốc</option>
            <option value="singapore">Singapore</option>
            <option value="thailand">Thái Lan</option>
            <option value="malaysia">Malaysia</option>
            <option value="philippines">Philippines</option>
            <option value="indonesia">Indonesia</option>
            <option value="china">Trung Quốc</option>
            <option value="australia">Úc</option>
            <option value="germany">Đức</option>
            <option value="france">Pháp</option>
            <option value="uk">Anh</option>
            <option value="canada">Canada</option>
          </select>
        </div>

        {/* Tổ chức */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tổ chức <span className="text-red-500">(*)</span>
          </label>
          <div className="flex items-center space-x-2">
            <select
              value={formData.organization}
              onChange={(e) =>
                handleInputChange("organization", e.target.value)
              }
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">- Tổ chức -</option>
              <option value="university">Trường Đại học</option>
              <option value="research-institute">Viện Nghiên cứu</option>
              <option value="government-agency">Cơ quan Chính phủ</option>
              <option value="ngo">Tổ chức Phi chính phủ</option>
              <option value="enterprise">Doanh nghiệp</option>
              <option value="international-organization">
                Tổ chức Quốc tế
              </option>
            </select>
            <button
              type="button"
              onClick={() => navigate("/add-new-organization")}
              className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 transition-colors"
              title="Thêm tổ chức mới"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Từ */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Từ
          </label>
          <div className="relative">
            <input
              type="date"
              value={formData.fromDate}
              onChange={(e) => handleInputChange("fromDate", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Đến */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Đến
          </label>
          <div className="relative">
            <input
              type="date"
              value={formData.toDate}
              onChange={(e) => handleInputChange("toDate", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Quý */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quý
          </label>
          <select
            value={formData.quarter}
            onChange={(e) => handleInputChange("quarter", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">- Quý -</option>
            <option value="q1">Quý 1</option>
            <option value="q2">Quý 2</option>
            <option value="q3">Quý 3</option>
            <option value="q4">Quý 4</option>
          </select>
        </div>
      </div>

      {/* Second Row - Three Columns with wider Ngân sách */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Tên đoàn */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tên đoàn
          </label>
          <input
            type="text"
            value={formData.delegationName}
            onChange={(e) =>
              handleInputChange("delegationName", e.target.value)
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Tên đoàn..."
          />
        </div>

        {/* Kinh phí */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kinh phí
          </label>
          <div className="relative">
            <input
              type="text"
              value={formData.budget}
              onChange={(e) => handleInputChange("budget", e.target.value)}
              className="w-full px-3 py-2 pr-12 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="..."
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <span className="text-gray-500 text-sm">VNĐ</span>
            </div>
          </div>
        </div>

        {/* Ngân sách - spans 2 columns */}
        <div className="lg:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Ngân sách
          </label>
          <input
            type="text"
            value={formData.budgetSource}
            onChange={(e) => handleInputChange("budgetSource", e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Ngân sách..."
          />
        </div>
      </div>
    </div>
  );
};

export default DelegationBasicInfo;
